import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReminderRepeatKind, RichTextDoc } from '@avslack/shared';
import {
  cancelScheduled,
  listDrafts,
  listGroups,
  listScheduled,
  saveDraft,
  scheduleMessage,
  updateProfile,
  type SessionInfo,
} from '../lib/api';

export const draftsKey = ['drafts'] as const;
export const scheduledKey = ['scheduled'] as const;
export const groupsKey = ['groups'] as const;

export function useDrafts() {
  const query = useQuery({ queryKey: draftsKey, queryFn: listDrafts, staleTime: 30_000 });

  const byChannel = useMemo(() => {
    const map = new Map<string, { text: string; body: RichTextDoc | null }>();
    for (const draft of query.data ?? []) {
      map.set(draft.channelId, { text: draft.text, body: draft.body });
    }
    return map;
  }, [query.data]);

  return { ...query, byChannel };
}

/**
 * Piszkozat mentese.
 *
 * A hivas kesleltetett (a szerkeszto hivja gepeles kozben), ezert nem
 * ervenytelenitjuk a listat minden mentesnel - az csak villogtatna a felulet
 * jelzeseit. A lista a kovetkezo betoltesnel frissul.
 */
export function useSaveDraft() {
  const queryClient = useQueryClient();

  return useCallback(
    async (channelId: string, value: { text: string; body?: RichTextDoc | undefined }) => {
      await saveDraft(channelId, {
        text: value.text,
        ...(value.body ? { body: value.body } : {}),
      }).catch(() => undefined);

      queryClient.setQueryData<{ channelId: string; text: string; body: RichTextDoc | null }[]>(
        draftsKey,
        (current) => {
          const others = (current ?? []).filter((d) => d.channelId !== channelId);
          if (!value.text.trim()) return others;
          return [
            ...others,
            {
              channelId,
              text: value.text,
              body: value.body ?? null,
              updatedAt: new Date().toISOString(),
            } as never,
          ];
        },
      );
    },
    [queryClient],
  );
}

export function useScheduled() {
  return useQuery({
    queryKey: scheduledKey,
    queryFn: listScheduled,
    staleTime: 30_000,
    /*
      AMIG VAN FUGGOBEN LEVO, ranezunk.

      Az uzenetet a szerver kuldi el, nem a bongeszo - a lista tehat magatol
      elavul, amikor lejar egy idozites. Enelkul az oldalsav tovabbra is
      "1 utemezett uzenet var"-t mutatott volna, amig a felhasznalo ujra nem
      tolti az oldalt.

      Ha nincs mire varni, a lekerdezes is megall: ures listanal nem
      kerdezgetunk feleslegesen.
    */
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 30_000 : false),
    refetchIntervalInBackground: false,
  });
}

export function useScheduleMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      text: string;
      body?: RichTextDoc;
      sendAt: string;
      isImportant?: boolean;
      repeat?: { kind: ReminderRepeatKind; days?: number[]; until?: string | null };
    }) => scheduleMessage(channelId!, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scheduledKey }),
  });
}

export function useCancelScheduled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelScheduled,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scheduledKey }),
  });
}

export function useGroups() {
  return useQuery({ queryKey: groupsKey, queryFn: listGroups, staleTime: 5 * 60_000 });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: (user) => {
      /**
       * A session-bejegyzes `{ user, mustChangePassword }` alaku, nem sima
       * felhasznalo. A `user` mezot cserelni kell, nem az egeszet felulirni -
       * kulonben a `mustChangePassword` eltunne, es a felulet a kovetkezo
       * olvasasnal `undefined.id`-ra futna.
       */
      queryClient.setQueryData<SessionInfo>(['session'], (current) =>
        current ? { ...current, user } : current,
      );
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
